import type { Tower } from "../entities/Tower.ts";
import { glowCache } from "./GlowSpriteCache.ts";
import { ELEMENT_RAMP } from "./designTokens.ts";
import { emissiveFill } from "./EmissiveMaterial.ts";
import { arenaMotionScale } from "../engine/MotionSettings.ts";

/**
 * Per-element tower body shapes. Pure draw functions extracted from
 * TowerRenderer to keep that file under the 300-line limit. Each takes the
 * canvas ctx, center (cx, cy), tile size, the tower, and the animation time.
 *
 * Motion contract (BL-176): the silhouette + emissive volume + hot core are
 * ALWAYS drawn (a complete, premium STILL frame); only the flair — flicker,
 * crackle, drift — scales by `arenaMotionScale()` so reduced-motion / heavy-load
 * reads as a clean static node (the kinetic node's rule). Colour is the central
 * `ELEMENT_RAMP` / `tower.colorLight` only (no inline hex); the near-white
 * `ramp.core` is the bloom seed the WebGL pass blows to white-hot.
 */

/** Full turn — factored so the minifier inlines one short name across the node
 *  draws (incl. the virus draw, which imports it) instead of repeating Math.PI*2. */
export const TAU = Math.PI * 2;

/** Fill a disc of radius `d` at polar (`a`, `r`) from (cx, cy): the shared
 *  core / knob primitive that keeps the reworked node draws lean. Caller sets
 *  fillStyle first. */
export function dotP(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  a: number,
  r: number,
  d: number,
): void {
  ctx.beginPath();
  ctx.arc(cx + Math.cos(a) * r, cy + Math.sin(a) * r, d, 0, TAU);
  ctx.fill();
}

/** Fire / Inferno-Spire (BL-176). A molten white-hot core wrapped in a crown of
 *  irregular, leaning, sharp-tipped flame tongues that lick + flicker — folded
 *  UP off a smooth base so it reads as rising flame, not a radial sun
 *  ("aggressive bloom, flickering intensity", ART §5). The tongues taper to dark
 *  cooled tips (the emissive ramp) and grow in count + reach with tier. */
export function drawMalwareNode(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  tower: Tower,
  time: number,
): void {
  const ramp = ELEMENT_RAMP[tower.element];
  const tier = Math.max(1, Math.min(6, tower.level || 1));
  const m = arenaMotionScale();
  // Two-octave combustion flicker — global breath of the whole flame (gated).
  const flick =
    1 + (Math.sin(time * 6.1 + cx) + Math.sin(time * 11.7)) * 0.04 * m;
  const rBase = size * 0.19;
  const rTip = size * 0.5 * flick;
  const tongues = 5 + tier; // 6 → 10
  const step = TAU / tongues;

  glowCache.draw(ctx, ramp.mid, rTip * 1.1, cx, cy, 0.42);

  // ── flame crown: irregular, leaning, sharp-tipped tongues — taller toward
  // screen-up (fire rises). No rigid spin: flames flicker in place. ──
  ctx.beginPath();
  for (let i = 0; i < tongues; i++) {
    const aV = i * step;
    const aT = (i + 0.5) * step + 0.26; // fixed tangential lean = the curl
    const aN = (i + 1) * step;
    // deterministic irregular height (broken even when frozen) + motion lick +
    // an upward fold so the crown reads as rising flame, not a symmetric flower.
    const hv = 0.74 + (Math.sin((i + 1) * 12.9898) * 0.5 + 0.5) * 0.42;
    const lick = 1 + Math.sin(time * 4.7 + i * 1.7) * 0.16 * m;
    const tip = rTip * hv * lick * (0.4 + 0.6 * (0.5 - 0.5 * Math.sin(aT)));
    const c1a = aV + step * 0.26;
    const c1r = (rBase + tip) * 0.4;
    const c2a = aT + step * 0.18;
    const c2r = tip * 0.82; // control near the tip → a sharp wisp, not a petal
    if (i === 0) ctx.moveTo(cx + Math.cos(aV) * rBase, cy + Math.sin(aV) * rBase);
    ctx.quadraticCurveTo(
      cx + Math.cos(c1a) * c1r,
      cy + Math.sin(c1a) * c1r,
      cx + Math.cos(aT) * tip,
      cy + Math.sin(aT) * tip,
    );
    ctx.quadraticCurveTo(
      cx + Math.cos(c2a) * c2r,
      cy + Math.sin(c2a) * c2r,
      cx + Math.cos(aN) * rBase,
      cy + Math.sin(aN) * rBase,
    );
  }
  ctx.closePath();
  ctx.fillStyle = emissiveFill(ctx, cx, cy, rTip, ramp);
  ctx.fill();

  // ── white-hot molten core: the emissive center + this near-white dot are the
  // bloom seed the WebGL pass blows to white-hot (grows with tier). ──
  ctx.fillStyle = ramp.core;
  dotP(ctx, cx, cy, 0, 0, rBase * (0.5 + tier * 0.05));
}

export function drawFirewallNode(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  tower: Tower,
): void {
  const tier = Math.max(1, Math.min(6, tower.level || 1));
  const r = size * 0.36;

  glowCache.draw(ctx, tower.color, r * 1.4, cx, cy, 0.4);

  ctx.fillStyle = "rgba(30,144,255,0.06)";
  ctx.beginPath();
  ctx.arc(cx, cy, r * 1.4, 0, Math.PI * 2);
  ctx.fill();

  // Body = lit volume (BL-26/27): emissive ramp across the firewall hexagon.
  ctx.fillStyle = emissiveFill(ctx, cx, cy, r, ELEMENT_RAMP[tower.element]);
  ctx.beginPath();
  for (let j = 0; j < 6; j++) {
    const angle = (j / 6) * Math.PI * 2 - Math.PI / 6;
    ctx.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
  }
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = tower.colorLight;
  for (let ring = 0; ring < tier; ring++) {
    const rr = r * (0.5 - ring * 0.08);
    if (rr <= r * 0.14) break;
    ctx.lineWidth = Math.max(0.9, 1.5 - ring * 0.15);
    ctx.beginPath();
    for (let j = 0; j < 6; j++) {
      const angle = (j / 6) * Math.PI * 2 - Math.PI / 6;
      ctx.lineTo(cx + Math.cos(angle) * rr, cy + Math.sin(angle) * rr);
    }
    ctx.closePath();
    ctx.globalAlpha = Math.max(0.35, 0.85 - ring * 0.12);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  glowCache.draw(
    ctx,
    tower.colorLight,
    r * (0.18 + tier * 0.035),
    cx,
    cy,
    0.35,
  );

  ctx.fillStyle = tower.colorLight;
  ctx.beginPath();
  ctx.arc(cx, cy, 2.2 + tier * 0.35, 0, Math.PI * 2);
  ctx.fill();
}

/** Deterministic [-1,1] pseudo-noise from a scalar seed (render-only crackle —
 *  no Math.random, so the bolt geometry is stable per tick and never touches sim
 *  determinism). Mirrors the kinetic node's `railNoise`. */
function empNoise(seed: number): number {
  const s = Math.sin(seed * 12.9898) * 43758.5453;
  return (s - Math.floor(s)) * 2 - 1;
}

/** One jagged lightning bolt from `r0` out to `r1` along `baseA`, kinked by
 *  deterministic noise (`seed`): a soft colored underlay + a near-white spine
 *  (the bloom seed). */
function drawBolt(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  baseA: number,
  r0: number,
  r1: number,
  seed: number,
  ramp: { core: string; mid: string },
): void {
  const segs = 3;
  const px: number[] = [];
  const py: number[] = [];
  for (let s = 0; s <= segs; s++) {
    const a = baseA + (s === 0 || s === segs ? 0 : empNoise(seed + s * 13) * 0.34);
    const rr = r0 + (r1 - r0) * (s / segs);
    px.push(cx + Math.cos(a) * rr);
    py.push(cy + Math.sin(a) * rr);
  }
  const stroke = (w: number): void => {
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.moveTo(px[0]!, py[0]!);
    for (let s = 1; s <= segs; s++) ctx.lineTo(px[s]!, py[s]!);
    ctx.stroke();
  };
  ctx.strokeStyle = ramp.mid; // soft colored body
  ctx.globalAlpha = 0.55;
  stroke(2.4);
  ctx.strokeStyle = ramp.core; // near-white spine (the bloom seed)
  ctx.globalAlpha = 1;
  stroke(1);
}

/** Lightning / Volt-Obelisk (BL-176). A charged faceted diamond core throwing
 *  sharp, high-contrast jagged bolts that crackle — re-kinking on a tick (never
 *  RNG, frozen at m = 0), each a near-white spine over a soft colored body, the
 *  "brief over-bright spikes" of ART §5. Tier adds bolt count + reach; the
 *  crackle is the lightning identity. */
export function drawEmpNode(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  tower: Tower,
  time: number,
): void {
  const ramp = ELEMENT_RAMP[tower.element];
  const tier = Math.max(1, Math.min(6, tower.level || 1));
  const m = arenaMotionScale();
  const r = size * 0.34;
  const coreR = r * 0.44;
  const bolts = 3 + tier; // 4 → 8

  glowCache.draw(ctx, ramp.mid, r * 1.1, cx, cy, 0.4);

  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // ── jagged bolts: deterministic crackle, frozen at m = 0 ──
  const tick = m > 0 ? Math.floor(time * 12) : 0; // re-kink 12×/s
  for (let j = 0; j < bolts; j++) {
    drawBolt(ctx, cx, cy, (j / bolts) * TAU, coreR, r * (1.22 + tier * 0.05), tick + j * 7, ramp);
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  // ── charged faceted core (diamond) — emissive volume + white-hot center ──
  ctx.beginPath();
  ctx.moveTo(cx, cy - coreR);
  ctx.lineTo(cx + coreR * 0.72, cy);
  ctx.lineTo(cx, cy + coreR);
  ctx.lineTo(cx - coreR * 0.72, cy);
  ctx.closePath();
  ctx.fillStyle = emissiveFill(ctx, cx, cy, coreR, ramp);
  ctx.fill();
  ctx.fillStyle = ramp.core;
  dotP(ctx, cx, cy, 0, 0, coreR * (0.3 + tier * 0.03));
}

export function drawSignalNode(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  tower: Tower,
  time: number,
): void {
  const tier = Math.max(1, Math.min(6, tower.level || 1));
  const r = size * 0.35;
  const t = time;
  const ringCount = 3 + Math.floor((tier - 1) / 2);

  // Outer glow via sprite
  glowCache.draw(ctx, tower.color, r * 1.4, cx, cy, 0.4);

  ctx.fillStyle = "rgba(180,74,255,0.06)";
  ctx.beginPath();
  ctx.arc(cx, cy, r * 1.4, 0, Math.PI * 2);
  ctx.fill();

  // Tiered concentric pulse rings (radar ping)
  for (let i = 0; i < ringCount; i++) {
    const phase = (t * (1.4 + tier * 0.06) + i * 0.21) % 1;
    const ringR = r * 0.4 + phase * r * 1.2;
    const alpha = 0.7 - phase * 0.55 - i * 0.06;
    ctx.strokeStyle = tower.color;
    ctx.lineWidth = 1.3 + tier * 0.05;
    ctx.globalAlpha = Math.max(0, alpha);
    ctx.beginPath();
    ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Central antenna dot
  glowCache.draw(ctx, tower.colorLight, r * 0.25, cx, cy, 0.7);
  const antennaR = r * (0.2 + tier * 0.018);
  // Body = lit volume (BL-26/27): emissive central antenna core.
  ctx.fillStyle = emissiveFill(ctx, cx, cy, antennaR, ELEMENT_RAMP[tower.element]);
  ctx.beginPath();
  ctx.arc(cx, cy, antennaR, 0, Math.PI * 2);
  ctx.fill();

  // Small crosshair lines
  ctx.strokeStyle = tower.colorLight;
  ctx.lineWidth = 1;
  const ch = r * (0.45 + tier * 0.06);
  const innerGap = ch * (0.28 - Math.min(0.12, tier * 0.02));
  ctx.beginPath();
  ctx.moveTo(cx - ch, cy);
  ctx.lineTo(cx - innerGap, cy);
  ctx.moveTo(cx + innerGap, cy);
  ctx.lineTo(cx + ch, cy);
  ctx.moveTo(cx, cy - ch);
  ctx.lineTo(cx, cy - innerGap);
  ctx.moveTo(cx, cy + innerGap);
  ctx.lineTo(cx, cy + ch);
  ctx.stroke();

  if (tier >= 4) {
    ctx.strokeStyle = tower.color;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.7;
    const d = r * 0.62;
    ctx.beginPath();
    ctx.moveTo(cx - d, cy - d);
    ctx.lineTo(cx - d * 0.48, cy - d * 0.48);
    ctx.moveTo(cx + d * 0.48, cy + d * 0.48);
    ctx.lineTo(cx + d, cy + d);
    ctx.moveTo(cx + d, cy - d);
    ctx.lineTo(cx + d * 0.48, cy - d * 0.48);
    ctx.moveTo(cx - d * 0.48, cy + d * 0.48);
    ctx.lineTo(cx - d, cy + d);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
}
